import { useAuth } from '../auth/useAuth'
import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { GroupService } from '../../lib/api'
import {
	ChangeGroupDataProps,
	DefaultGroupParamsProps,
	GetGroupDataResponse,
	MoveItemsDataParamsProps,
} from '../../lib/api/types/group.interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'

export const useCreateGroup = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['groupCreate'],
		mutationFn: ({ data }: { data: ChangeGroupDataProps }) =>
			GroupService.create({ data }),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: (_, { data }) =>
			queryClient.invalidateQueries({
				queryKey: data?.parentId ? ['group'] : ['organizationGroups'],
				exact: false,
			}),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRenameGroup = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['groupRename'],
		mutationFn: ({ data, id }: { data: { name: string }; id: string }) =>
			GroupService.rename({ data, id }),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationGroups'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteGroup = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['groupDelete'],
		mutationFn: ({ id }: DefaultGroupParamsProps) =>
			GroupService.delete({ id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ['group', id],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useGroup = (
	{ id }: DefaultGroupParamsProps,
	options?: Partial<QueryObserverOptions<GetGroupDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetGroupDataResponse>({
		queryKey: ['group', id],
		queryFn: () => GroupService.get({ id }),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useUpdateGroupItems = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['groupUpdateItems'],
		mutationFn: ({
			items,
			id,
		}: {
			items: { id: string; type: number }[]
			id: string
		}) => GroupService.updateGroupItems({ items, id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['group'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useMoveItemsBetweenGroups = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['groupsMoveItems'],
		mutationFn: ({
			data,
			destinationGroupId,
			invalidate,
		}: {
			data: MoveItemsDataParamsProps
			destinationGroupId: string | null
			invalidate?: boolean
		}) => GroupService.moveItems({ data, destinationGroupId }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { invalidate }) => {
			invalidate &&
				queryClient.invalidateQueries({
					queryKey: ['group'],
				})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteItemsFromGroup = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['groupsMoveItems'],
		mutationFn: ({
			data,
			destinationGroupId,
		}: {
			data: MoveItemsDataParamsProps
			destinationGroupId: string | null
		}) => GroupService.moveItems({ data, destinationGroupId }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['group'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
